import { useQuery, useZero } from "@rocicorp/zero/react";
import { useRef, useState } from "react";
import { sharedMutators } from "../../../shared/mutators";
import { queries } from "../../../shared/queries";
import {
  hashSkillBundle,
  skillBundleWithLocalName,
  type SkillBundle,
} from "../../../shared/skillGitUpdates";
import {
  parseStoredSkillFile,
  serializeStoredSkillFile,
  SKILL_MD_PATH,
} from "../../../shared/skills/spec";
import { classifyZeroResult } from "../../lib/zeroResultError";
import { Button } from "../Button";
import { ButtonPress } from "../Button/ButtonPress";
import { CodeDiffFooter } from "../CodeDiffFooter";
import type { CodeEditorHandle } from "../CodeEditor";
import { useCodeDiffNavigation } from "../CodeEditor/useCodeDiffNavigation";
import { useCodeEditorSearch } from "../CodeEditor/useCodeEditorSearch";
import { ConnectorLogoContainer } from "../ConnectorLogoContainer";
import { HeaderTitleStack } from "../HeaderTitleStack";
import { ToggleButton, ToggleGroup } from "../HeadingToggle";
import { Icon } from "../Icon";
import { Modal } from "../Modal";
import { StepDetails } from "../StepDetails";
import { buildDiff, mergeFiles } from "../stepFilesDiff";
import { Text } from "../Text";
import { Tooltip } from "../Tooltip";
import { SkillFileEditor } from "./SkillFileEditor";
import { SkillFilePicker } from "./SkillFilePicker";
import {
  Body,
  Footer,
  FooterRight,
  Header,
  HeaderLeft,
  HeaderRight,
  skillModalStyle,
} from "./SkillModalLayout";
import { downloadStoredFile } from "./skillFileIO";

type Props = {
  skillId: string;
  connectedAppId: string | null;
  onClose: () => void;
};
export function SkillUpdateReview(props: Props) {
  const [skill, details] = useQuery(
    queries.loadSkillUpdate({ skillId: props.skillId })
  );
  if (
    !skill?.gitInstallation?.candidateBlob ||
    !skill.gitInstallation.candidateHash ||
    skill.files.some((file) => !file.blob)
  )
    return (
      <Modal open onClose={props.onClose} style={skillModalStyle}>
        <Header>
          <Text variant="text-regular-md">Review skill update</Text>
        </Header>
        <Body role="status">
          <Text>
            {details.type === "unknown"
              ? "Loading update…"
              : "This update is unavailable. Close this review and check for updates again."}
          </Text>
        </Body>
        <Footer>
          <div />
          <ButtonPress size="l" variant="secondary" onPress={props.onClose}>
            Close
          </ButtonPress>
        </Footer>
      </Modal>
    );
  return (
    <ReviewContent
      {...props}
      name={skill.name}
      installed={Object.fromEntries(
        skill.files.map((file) => [
          file.path,
          parseStoredSkillFile(file.blob!.content),
        ])
      )}
      candidate={JSON.parse(skill.gitInstallation.candidateBlob.content)}
      candidateHash={skill.gitInstallation.candidateHash}
      commit={skill.gitInstallation.candidateCommit ?? ""}
    />
  );
}

function ReviewContent({
  installed,
  candidate,
  candidateHash,
  commit,
  name,
  skillId,
  connectedAppId,
  onClose,
}: Props & {
  installed: SkillBundle;
  candidate: SkillBundle;
  candidateHash: string;
  commit: string;
  name: string;
}) {
  const z = useZero();
  const [review] = useState(() => ({
    installed,
    incoming: skillBundleWithLocalName(candidate, name),
    candidateHash,
    commit,
  }));
  const files = mergeFiles(
    Object.entries(review.incoming).map(([path, file]) => ({
      path,
      content: serializeStoredSkillFile(file),
    })),
    Object.entries(review.installed).map(([path, file]) => ({
      path,
      content: serializeStoredSkillFile(file),
    }))
  )
    .filter((file) => file.kind !== "unchanged")
    .toSorted((a, b) => {
      if (a.path === SKILL_MD_PATH) return -1;
      if (b.path === SKILL_MD_PATH) return 1;
      return a.path.localeCompare(b.path);
    });
  const [selected, setSelected] = useState(files[0]?.path ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const search = useCodeEditorSearch();
  const oldFile = selected ? review.installed[selected] : undefined;
  const newFile = selected ? review.incoming[selected] : undefined;
  const binary =
    oldFile?.encoding === "base64" || newFile?.encoding === "base64";
  const diff = binary
    ? null
    : buildDiff(oldFile?.content ?? "", newFile?.content ?? "");
  const editorRef = useRef<CodeEditorHandle>(null);
  const diffNavigation = useCodeDiffNavigation({
    enabled: true,
    files,
    selectedPath: selected,
    onSelectPath: setSelected,
    lineOrigins: diff?.lineOrigins,
    editorRef,
  });
  const downloadFile = newFile ?? oldFile;
  const apply = async () => {
    setSaving(true);
    setError(null);
    try {
      const installedHash = (await hashSkillBundle(review.installed)).hash;
      const result = await z.mutate(
        sharedMutators.applySkillUpdate({
          skillId,
          connectedAppId,
          candidateHash: review.candidateHash,
          candidateCommit: review.commit,
          installedHash,
        })
      ).server;
      if (result.type === "error") {
        const decision = classifyZeroResult(result);
        setError(
          decision.kind === "toast"
            ? (decision.message ?? "Could not apply update")
            : "Could not apply update"
        );
        setSaving(false);
        return;
      }
      onClose();
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : "Could not apply update"
      );
      setSaving(false);
    }
  };
  return (
    <Modal open onClose={onClose} style={skillModalStyle}>
      <Header>
        <HeaderLeft>
          <ConnectorLogoContainer
            size={32}
            borderRadius={8}
            hoverBackground={false}
          >
            <Icon name="skills" size={16} />
          </ConnectorLogoContainer>
          <HeaderTitleStack>
            <Text
              variant="text-regular-md"
              color="var(--text-primary)"
              ellipsis
            >
              {name}
            </Text>
            <Text variant="text-small" color="var(--text-secondary)" ellipsis>
              Review skill update
            </Text>
          </HeaderTitleStack>
        </HeaderLeft>
        <HeaderRight>
          <ButtonPress
            size="l"
            variant="secondary"
            icon="x"
            aria-label="Close review"
            onPress={onClose}
          />
        </HeaderRight>
      </Header>
      <Body>
        <search.Provider>
          <StepDetails
            heading={
              files.length > 0 && (
                <SkillFilePicker
                  files={files}
                  selectedPath={selected}
                  onSelectPath={setSelected}
                  selectedBinary={binary}
                />
              )
            }
            headingRight={
              <ToggleGroup>
                {search.toggleButton}
                {downloadFile && (
                  <Tooltip content="Download">
                    <ToggleButton
                      aria-label="Download file"
                      onPress={() => downloadStoredFile(selected, downloadFile)}
                    >
                      <Icon name="circle-arrow-down" size={12} />
                    </ToggleButton>
                  </Tooltip>
                )}
              </ToggleGroup>
            }
            subheader={search.subheader ?? undefined}
            footer={
              files.length > 0 ? <CodeDiffFooter {...diffNavigation} /> : null
            }
            flushBody={!binary && files.length > 0}
            body={
              !files.length ? (
                <Text>The installed files already match this version.</Text>
              ) : binary ? (
                <Text>
                  Binary file{" "}
                  {oldFile && newFile
                    ? "changed"
                    : newFile
                      ? "added"
                      : "deleted"}
                  .
                </Text>
              ) : (
                <SkillFileEditor
                  ref={editorRef}
                  key={selected}
                  filePath={selected}
                  value={diff!.doc}
                  diffRanges={diff!.diffRanges}
                  onVisibleLineChange={diffNavigation.onVisibleLineChange}
                  readOnly
                />
              )
            }
          />
        </search.Provider>
      </Body>
      <Footer>
        <div>
          {error && (
            <Text variant="text-small" color="var(--red-600)">
              {error}
            </Text>
          )}
        </div>
        <FooterRight>
          <ButtonPress size="l" variant="secondary" onPress={onClose}>
            Cancel
          </ButtonPress>
          <Button size="l" disabled={saving} onClick={() => void apply()}>
            {saving ? "Updating…" : "Apply update"}
          </Button>
        </FooterRight>
      </Footer>
    </Modal>
  );
}
